import { utcMidnightSecondsToCalendarDay } from "../utils/epochTime";
import { ReactComponent as BackIcon } from "../assets/images/backIcon.svg";
import { ReactComponent as ForwordIcon } from "../assets/images/forward.svg";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { ArrowForward, ChevronLeft, ChevronRight, Edit, Check, Close } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getClientWeightTable, updateWeightTable } from "../services/TraineeService";
import { getTraineeFoodLog } from "../services/LogFoodService";
import moment from "moment";
import Loader from "../componenets/Loader";
import { getErrorMessage } from "../enums/ErrorMessages";
import { useMessageContext } from "../contexts/MessageContext";
import { WeeklyRanges } from "../enums/WeeklyRanges";
import { GoalTypes, GOAL_DEFAULT_WEEKLY_RANGE } from "../enums/GoalTypes";
import { useNutritionDisplayMode } from "../contexts/NutritionDisplayContext";
import { POINTS, energyFigure, energyFromInput, energyLabel, energyToInput } from "../componenets/nutrition/v2/ui/nutritionUnits";
// import GoalsEditModal from "../componenets/weight-tracking/GoalsEditModal";

const DAYS = [
  { key: "sunday", label: "א'" },
  { key: "monday", label: "ב'" },
  { key: "tuesday", label: "ג'" },
  { key: "wednesday", label: "ד'" },
  { key: "thursday", label: "ה'" },
  { key: "friday", label: "ו'" },
  { key: "saturday", label: "ש'" }
];

const ClientWeightTrackPage = () => {
  let navigate = useNavigate();
  const params = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const nutritionMode = useNutritionDisplayMode();
  const pointsMode = nutritionMode === POINTS;
  // Calories as points (kcal ÷ 100) for a points company. The goal is still stored in kcal.
  const energyNum = (kcal) => (pointsMode ? energyFigure(kcal, nutritionMode).text : kcal.toLocaleString());

  const [currentWeek, setCurrentWeek] = useState(moment().day(0));
  const [weightTracks, setWeightTracks] = useState([]);
  const [calorieData, setCalorieData] = useState({});
  const [reload, setReload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAllDays, setShowAllDays] = useState(false);
  const [showStepsDetails, setShowStepsDetails] = useState(false);
  const [showSleepDetails, setShowSleepDetails] = useState(false);
  const [showCaloriesDetails, setShowCaloriesDetails] = useState(false);
  // const [goalsModalOpen, setGoalsModalOpen] = useState(false);
  // const [editingWeekData, setEditingWeekData] = useState(null);
  const [mobileWeekIndex, setMobileWeekIndex] = useState(0); // For mobile week navigation
  const [editingWeekDate, setEditingWeekDate] = useState(null); // which week is being edited
  const [editedWeekData, setEditedWeekData] = useState(null); // temp storage for edits

  const { notificationHandler } = useMessageContext();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch weight data
        const weightRes = await getClientWeightTable(
          params.userId,
          moment(currentWeek).subtract(6, "weeks"),
          moment(currentWeek).add(1, "week")
        );
        const weeks = weightRes.data.weeks;
        setWeightTracks(weeks);

        // Fetch food log (calorie intake) for each week
        const calorieMap = {};
        await Promise.all(
          weeks.slice(0, -1).map(async (week) => {
            // weekDate is a CALENDAR DAY the server emits at UTC midnight, so decode it at UTC.
            // moment(...).format() renders in the viewer's LOCAL zone, which for anyone west of
            // UTC shifts it back a day (verified: America/New_York turned 2026-07-26 into
            // 2026-07-25) and fetched the WRONG week's calories.
            const weekDateStr = utcMidnightSecondsToCalendarDay(week.weekDate);
            const foodLogRes = await getTraineeFoodLog(params.userId, weekDateStr);
            if (foodLogRes.success) {
              calorieMap[week.weekDate] = foodLogRes.data;
            }
          })
        );
        setCalorieData(calorieMap);
      } catch (e) {
        notificationHandler.error(
          getErrorMessage(e.response?.data?.errorCode)
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentWeek, reload, params.userId]);

  // Helper functions
  const getCalorieAverage = (weekDate) => {
    const logs = (calorieData[weekDate]?.dailyLogs || [])
      .filter(l => l.menu?.nutritionSummary?.calories > 0);
    if (logs.length === 0) return null;
    const total = logs.reduce((sum, l) => sum + l.menu.nutritionSummary.calories, 0);
    return Math.round(total / logs.length);
  };

  const getWeightAverage = (week) => {
    const weights = DAYS
      .map((day) => week[day.key + "Weight"])
      .filter((weight) => weight && weight > 0);
    if (weights.length === 0) return null;
    const sum = weights.reduce((acc, weight) => acc + parseFloat(weight), 0);
    return (sum / weights.length).toFixed(1);
  };

  const getStepsAverage = (week) => {
    const steps = DAYS
      .map((day) => week[day.key + "Steps"])
      .filter((step) => step && step > 0);
    if (steps.length === 0) return null;
    const sum = steps.reduce((acc, step) => acc + parseInt(step), 0);
    return Math.round(sum / steps.length);
  };

  const getSleepAverage = (week) => {
    const sleepValues = DAYS
      .map((day) => week[day.key + "Sleep"])
      .filter((sleep) => sleep && sleep > 0);
    if (sleepValues.length === 0) return null;
    const sum = sleepValues.reduce((acc, sleep) => acc + parseInt(sleep), 0);
    return Math.round(sum / sleepValues.length);
  };

  const getTrend = (weekIndex) => {
    const currentAvg = parseFloat(getWeightAverage(weightTracks[weekIndex]));
    if (!currentAvg || weekIndex >= weightTracks.length - 1) return null;

    const previousAvg = parseFloat(getWeightAverage(weightTracks[weekIndex + 1]));
    if (!previousAvg) return null;

    return (currentAvg - previousAvg).toFixed(2);
  };

  const getTrendColor = (weekIndex) => {
    const trend = parseFloat(getTrend(weekIndex));
    if (isNaN(trend)) return "#64748B";

    const goal = weightTracks[weekIndex].goal;
    const goalTrend = GoalTypes.find((g) => g.value === goal)?.trend;
    if (goalTrend === -1) {
      // cut goals: losing (or holding) weight is good
      return trend <= 0 ? "#10B981" : "#EF4444";
    } else if (goalTrend === 1) {
      // bulk goals: gaining (or holding) weight is good
      return trend >= 0 ? "#10B981" : "#EF4444";
    } else if (goalTrend === 0) {
      // maintenance: good if within the selected weekly range
      const range = WeeklyRanges.find((x) => x.value === weightTracks[weekIndex].weeklyRange);
      if (range && trend >= range.min && trend <= range.max) {
        return "#10B981";
      }
      return "#EF4444";
    }
    return "#64748B";
  };

  const getGoalRangeText = (week) => {
    const range = WeeklyRanges.find((x) => x.value === week.weeklyRange);
    if (range) return `יעד: ${range.name}`;
    return "";
  };

  const formatDateRange = (week) => {
    // Parse the UTC-decoded calendar day, so the label names the same week the data belongs to.
    // moment(secs * 1000) alone localizes and shows the previous day west of UTC.
    const weekDate = moment(utcMidnightSecondsToCalendarDay(week.weekDate), "YYYY-MM-DD");
    const startDate = weekDate.format("D");
    const endDate = weekDate.clone().add(6, "days").format("D");
    const month = weekDate.format("MMM").replace(".", "'");
    return `${startDate}-${endDate} ${month}`;
  };

  const calculatePercentage = (average, goal) => {
    if (!average || !goal || goal === 0) return 0;
    return Math.min(Math.round((average / goal) * 100), 100);
  };

  // Navigation handlers
  const nextXWeeks = () => {
    setCurrentWeek((prev) => moment(prev).add(7, "weeks"));
  };

  const backXWeeks = () => {
    setCurrentWeek((prev) => moment(prev).subtract(7, "weeks"));
  };

  // Goals Modal handlers (commented out - using inline editing instead)
  // const openGoalsModal = () => {
  //   if (weightTracks.length > 0) {
  //     setEditingWeekData({ ...weightTracks[0] });
  //     setGoalsModalOpen(true);
  //   }
  // };

  // const closeGoalsModal = () => {
  //   setGoalsModalOpen(false);
  //   setEditingWeekData(null);
  // };

  // const handleWeekDataChange = (newData) => {
  //   setEditingWeekData(newData);
  // };

  // const saveGoals = () => {
  //   if (!editingWeekData) return;
  //   updateWeightTable(params.userId, editingWeekData)
  //     .then(() => {
  //       closeGoalsModal();
  //       notificationHandler.success("יעדים נשמרו בהצלחה");
  //       setReload((prev) => !prev);
  //     })
  //     .catch((e) =>
  //       notificationHandler.error(getErrorMessage(e.response?.data?.errorCode))
  //     );
  // };

  // Inline editing functions
  const startEditingWeek = (week) => {
    setEditingWeekDate(week.weekDate);
    setEditedWeekData({ ...week });
  };

  const cancelEditing = () => {
    setEditingWeekDate(null);
    setEditedWeekData(null);
  };

  const updateEditedField = (field, value) => {
    setEditedWeekData(prev => ({ ...prev, [field]: value }));
  };

  const saveWeekEdits = async () => {
    if (!editedWeekData) return;
    try {
      await updateWeightTable(params.userId, editedWeekData);
      setEditingWeekDate(null);
      setEditedWeekData(null);
      setReload(prev => !prev);
      notificationHandler.success("השינויים נשמרו בהצלחה");
    } catch (e) {
      notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
    }
  };

  // Goal type labels — derived from the shared GoalTypes enum (the 8 intensity-based targets).
  const goalLabels = GoalTypes.reduce((acc, g) => ({ ...acc, [g.value]: g.name }), {});

  // Which weekly ranges are valid for a goal: cuts → minus ranges, bulks → plus ranges,
  // maintenance → the ±0.2 band only. Mirrors the mobile app's range constraint.
  const isRangeDisabledForGoal = (rangeValue, goalValue) => {
    const trend = GoalTypes.find((g) => g.value === goalValue)?.trend;
    if (trend == null) return false; // no goal picked yet — allow everything
    const range = WeeklyRanges.find((r) => r.value === rangeValue);
    if (!range) return false;
    // Cut → only fully-negative ranges; bulk → only fully-positive ranges.
    if (trend < 0) return !(range.min < 0 && range.max < 0);
    if (trend > 0) return !(range.min > 0 && range.max > 0);
    return rangeValue !== "MAINTENANCE"; // maintenance → only the ±0.2 range
  };

  // Metric Summary Card Component
  const MetricCard = ({ type, average, goal, dailyData, expanded, onToggleExpand, isEditing, onFieldChange, onGoalChange, goalFieldName }) => {
    const configs = {
      steps: {
        icon: <DirectionsWalkIcon sx={{ fontSize: 16 }} />,
        label: "צעדים",
        iconBg: "#10B981",
        formatValue: (val) => val?.toLocaleString() || "-",
        formatGoal: (goalVal) => goalVal ? goalVal.toLocaleString() : "-",
        formatGoalForEdit: (goalVal) => goalVal || "",
        formatDaily: (val) => val && val > 0 ? val.toLocaleString() : "—",
        suffix: "Steps",
        inputType: "number",
        parseValue: (v) => v ? parseInt(v) : null,
        parseGoal: (v) => v ? parseInt(v) : null,
        goalPlaceholder: "יעד צעדים"
      },
      sleep: {
        icon: <NightsStayIcon sx={{ fontSize: 16 }} />,
        label: "שינה",
        iconBg: "#8B5CF6",
        formatValue: (val) => val ? `${(val / 60).toFixed(1)} שעות` : "-",
        formatGoal: (goalVal) => goalVal ? `${(goalVal / 60).toFixed(0)} שעות` : "-",
        formatGoalForEdit: (goalVal) => goalVal ? (goalVal / 60).toFixed(1) : "",
        formatDaily: (val) => val && val > 0 ? `${(val / 60).toFixed(1)} שעות` : "—",
        formatForEdit: (val) => val ? (val / 60).toFixed(1) : "", // convert minutes to hours for editing
        suffix: "Sleep",
        inputType: "number",
        parseValue: (v) => v ? Math.round(parseFloat(v) * 60) : null, // convert hours back to minutes for storage
        parseGoal: (v) => v ? Math.round(parseFloat(v) * 60) : null,
        goalPlaceholder: "שעות"
      },
      calories: {
        icon: <LocalFireDepartmentIcon sx={{ fontSize: 16 }} />,
        label: `צריכת ${energyLabel(nutritionMode)}`,
        iconBg: "#F59E0B",
        formatValue: (val) => (val ? energyNum(val) : val?.toLocaleString()) || "-",
        formatGoal: (goalVal) => goalVal ? energyNum(goalVal) : "-",
        // The goal field takes points in points mode (uncontrolled, read on blur, so a half-typed
        // "18." is never round-tripped through kcal) and stores the kcal they mean.
        formatGoalForEdit: (goalVal) => goalVal ? energyToInput(goalVal, nutritionMode) : "",
        formatDaily: (val) => val && val > 0 ? energyNum(val) : "—",
        suffix: "Calories",
        inputType: "number",
        parseValue: (v) => v ? parseInt(v) : null,
        parseGoal: (v) => v ? (pointsMode ? energyFromInput(parseFloat(v), nutritionMode) : parseInt(v)) : null,
        goalPlaceholder: `יעד ${energyLabel(nutritionMode)}`
      }
    };

    const config = configs[type];
    const percentage = calculatePercentage(average, goal);
    const progressColor = percentage >= 100 ? "#10B981" : "#F59E0B";
    const hasExpandFeature = dailyData && onToggleExpand;

    return (
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          padding: "12px",
          border: "1px solid #E2E8F0",
          minWidth: "140px"
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1, mb: 1 }}>
            <Box
                sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "6px",
                    backgroundColor: config.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#FFFFFF"
                }}
            >
                {config.icon}
            </Box>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E293B" }}>
            {config.label}
          </Typography>
        </Box>

        {/* Average Row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography sx={{ fontSize: "12px", color: "#64748B" }}>ממוצע</Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1E293B" }}>
            {config.formatValue(average)}
          </Typography>
        </Box>

        {/* Goal Row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography sx={{ fontSize: "12px", color: "#64748B" }}>יעד</Typography>
          {isEditing && onGoalChange && goalFieldName ? (
            <TextField
              defaultValue={config.formatGoalForEdit(goal)}
              onBlur={(e) => onGoalChange(goalFieldName, config.parseGoal(e.target.value))}
              type="number"
              size="small"
              placeholder={config.goalPlaceholder}
              inputProps={{ step: type === "sleep" ? "0.5" : type === "calories" && pointsMode ? "0.1" : "1", style: { textAlign: "center", padding: "2px 4px", fontSize: "11px" } }}
              sx={{ width: "70px", "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
            />
          ) : (
            <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
              {config.formatGoal(goal)}
            </Typography>
          )}
        </Box>

        {/* Progress Bar */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: progressColor, minWidth: "30px" }}>
            {percentage}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              backgroundColor: "#E2E8F0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: progressColor,
                borderRadius: 3
              }
            }}
          />
        </Box>

        {/* Expand/Collapse Button */}
        {hasExpandFeature && (
          <Box
            onClick={onToggleExpand}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              mt: 1,
              pt: 1,
              borderTop: "1px solid #E2E8F0",
              cursor: "pointer",
              "&:hover": { backgroundColor: "#F8FAFC" },
              borderRadius: "4px"
            }}
          >
            <Typography sx={{ fontSize: "11px", color: "#64748B" }}>
              {expanded ? "הסתר ימים" : "הצג ימים"}
            </Typography>
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 16, color: "#64748B" }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16, color: "#64748B" }} />
            )}
          </Box>
        )}

        {/* Daily Data Collapse */}
        {hasExpandFeature && (
          <Collapse in={expanded}>
            <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #E2E8F0" }}>
              {dailyData.map((item) => (
                <Box
                  key={item.fieldName}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 0.5
                  }}
                >
                  <Typography sx={{ fontSize: "11px", color: "#64748B" }}>
                    יום {item.day}
                  </Typography>
                  {isEditing && onFieldChange ? (
                    <TextField
                      defaultValue={config.formatForEdit ? config.formatForEdit(item.value) : (item.value || "")}
                      onBlur={(e) => onFieldChange(item.fieldName, config.parseValue(e.target.value))}
                      type="number"
                      size="small"
                      placeholder={type === "sleep" ? "שעות" : ""}
                      inputProps={{ step: type === "sleep" ? "0.5" : "1", style: { textAlign: "center", padding: "2px 4px", fontSize: "11px" } }}
                      sx={{ width: "70px", "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#1E293B" }}>
                      {config.formatDaily(item.value)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  const displayWeeks = weightTracks.slice(0, -1);

  // Mobile navigation for weeks
  const goToNextMobileWeek = () => {
    if (mobileWeekIndex > 0) {
      setMobileWeekIndex(mobileWeekIndex - 1);
    }
  };

  const goToPrevMobileWeek = () => {
    if (mobileWeekIndex < displayWeeks.length - 1) {
      setMobileWeekIndex(mobileWeekIndex + 1);
    }
  };

  // Mobile Week Card Component
  const MobileWeekCard = ({ week, weekIndex }) => {
    const trend = getTrend(weekIndex);
    const trendColor = getTrendColor(weekIndex);

    return (
      <Box sx={{ direction: "rtl" }}>
        {/* Week Header */}
        <Box sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          padding: "16px",
          marginBottom: "12px"
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#1E293B" }}>
              שבוע {displayWeeks.length - weekIndex}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#64748B" }}>
              {formatDateRange(week)}
            </Typography>
          </Box>

          {/* Weight Average */}
          <Box sx={{
            backgroundColor: "#F8FAFC",
            borderRadius: "8px",
            padding: "16px",
            textAlign: "center",
            mb: 2
          }}>
            <Typography sx={{ fontSize: "12px", color: "#64748B", mb: 0.5 }}>
              ממוצע שבועי
            </Typography>
            <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#1E293B" }}>
              {getWeightAverage(week) ? `${getWeightAverage(week)} ק"ג` : "—"}
            </Typography>
          </Box>

          {/* Trend */}
          {trend !== null && (
            <Box sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              padding: "12px",
              backgroundColor: trendColor === "#10B981" ? "#F0FDF4" : "#FEF2F2",
              borderRadius: "8px"
            }}>
              {parseFloat(trend) < 0 ? (
                <TrendingDownIcon sx={{ color: trendColor, fontSize: 24 }} />
              ) : (
                <TrendingUpIcon sx={{ color: trendColor, fontSize: 24 }} />
              )}
              <Typography sx={{ fontSize: "16px", fontWeight: 600, color: trendColor }}>
                {trend > 0 ? "+" : ""}{trend} ק"ג
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#64748B", mr: 1 }}>
                {getGoalRangeText(week)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Daily Weights */}
        <Box sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          overflow: "hidden",
          marginBottom: "12px"
        }}>
          <Box
            onClick={() => setShowAllDays(!showAllDays)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              cursor: "pointer",
              backgroundColor: "#F8FAFC"
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}>
              שקילות יומיות
            </Typography>
            {showAllDays ? <ExpandLessIcon sx={{ color: "#64748B" }} /> : <ExpandMoreIcon sx={{ color: "#64748B" }} />}
          </Box>
          <Collapse in={showAllDays}>
            <Box sx={{ padding: "8px 16px 16px" }}>
              {DAYS.map((day) => (
                <Box
                  key={day.key}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0",
                    borderBottom: "1px solid #F1F5F9",
                    "&:last-child": { borderBottom: "none" }
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#64748B" }}>
                    יום {day.label}
                  </Typography>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}>
                    {week[day.key + "Weight"] ? `${week[day.key + "Weight"]} ק"ג` : "—"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>

        {/* Metrics Cards */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <MetricCard
            type="calories"
            average={getCalorieAverage(week.weekDate)}
            goal={week.caloriesGoal}
            dailyData={DAYS.map(day => {
              const log = calorieData[week.weekDate]?.dailyLogs?.find(
                l => l.dayOfWeek?.toLowerCase() === day.key
              );
              return { day: day.label, value: log?.menu?.nutritionSummary?.calories, fieldName: day.key };
            })}
            expanded={showCaloriesDetails}
            onToggleExpand={() => setShowCaloriesDetails(!showCaloriesDetails)}
          />
          <MetricCard
            type="steps"
            average={getStepsAverage(week)}
            goal={week.stepsGoal}
            dailyData={DAYS.map(day => ({ day: day.label, value: week[day.key + "Steps"], fieldName: day.key + "Steps" }))}
            expanded={showStepsDetails}
            onToggleExpand={() => setShowStepsDetails(!showStepsDetails)}
          />
          <MetricCard
            type="sleep"
            average={getSleepAverage(week)}
            goal={week.sleepGoalMinutes}
            dailyData={DAYS.map(day => ({ day: day.label, value: week[day.key + "Sleep"], fieldName: day.key + "Sleep" }))}
            expanded={showSleepDetails}
            onToggleExpand={() => setShowSleepDetails(!showSleepDetails)}
          />
        </Box>
      </Box>
    );
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <Box sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh", padding: "12px", direction: "rtl" }}>
        <Loader loading={loading} />

        {/* Mobile Header */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() => navigate(`/app/clients/${params.userId}`)}
              sx={{ color: "#64748B", padding: "8px" }}
            >
              <ArrowForward />
            </IconButton>
            <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#1E293B" }}>
              מעקב שקילות
            </Typography>
          </Box>
          {/* Button hidden - using inline editing instead
          <Button
            variant="contained"
            onClick={openGoalsModal}
            size="small"
            sx={{
              backgroundColor: "#10B981",
              borderRadius: "8px",
              padding: "6px 12px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "12px",
              "&:hover": { backgroundColor: "#059669" }
            }}
          >
            עדכן יעדים
          </Button>
          */}
        </Box>

        {/* Week Navigation */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          border: "1px solid #E2E8F0",
          padding: "12px 16px",
          marginBottom: "16px"
        }}>
          <IconButton
            onClick={goToPrevMobileWeek}
            disabled={mobileWeekIndex >= displayWeeks.length - 1}
            sx={{ color: "#64748B", padding: "8px" }}
          >
            <ChevronRight />
          </IconButton>

          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}>
              {displayWeeks.length > 0 ? `שבוע ${displayWeeks.length - mobileWeekIndex}` : ""}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
              {displayWeeks[mobileWeekIndex] ? formatDateRange(displayWeeks[mobileWeekIndex]) : ""}
            </Typography>
          </Box>

          <IconButton
            onClick={goToNextMobileWeek}
            disabled={mobileWeekIndex <= 0}
            sx={{ color: "#64748B", padding: "8px" }}
          >
            <ChevronLeft />
          </IconButton>
        </Box>

        {/* Date Range Navigation */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          marginBottom: "16px"
        }}>
          <Box onClick={nextXWeeks} sx={{ cursor: "pointer", padding: "8px" }}>
            <ForwordIcon />
          </Box>
          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#64748B" }}>
            {moment(currentWeek).subtract(6, "weeks").format("D MMM").replace(".", "'")} - {currentWeek.format("D MMM").replace(".", "'")}
          </Typography>
          <Box onClick={backXWeeks} sx={{ cursor: "pointer", padding: "8px" }}>
            <BackIcon />
          </Box>
        </Box>

        {/* Current Week Card */}
        {displayWeeks[mobileWeekIndex] && (
          <MobileWeekCard
            week={displayWeeks[mobileWeekIndex]}
            weekIndex={mobileWeekIndex}
          />
        )}

        </Box>
    );
  }

  // Desktop Layout
  return (
    <Box sx={{ backgroundColor: "#F8FAFC", minHeight: "100vh", padding: "20px" }}>
      <Loader loading={loading} />

      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <IconButton
            onClick={() => navigate(`/app/clients/${params.userId}`)}
            sx={{
              color: "#64748B",
              "&:hover": {
                backgroundColor: "#F1F5F9",
                color: "#1E293B"
              }
            }}
          >
            <ArrowForward />
          </IconButton>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: "28px", fontWeight: 700, color: "#1E293B" }}>
              מעקב שקילות
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#64748B" }}>
              סקירה שבועית של שקילות, צעדים, שינה ויעדים.
            </Typography>
          </Box>
        </Box>
        {/* Button hidden - using inline editing instead
        <Button
          variant="contained"
          onClick={openGoalsModal}
          sx={{
            backgroundColor: "#10B981",
            borderRadius: "8px",
            padding: "10px 20px",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#059669" }
          }}
        >
          עדכן יעדים שבועיים
        </Button>
        */}
      </Box>

      {/* Main Table Card */}
      <Box
        sx={{
          backgroundColor: "#F9FAFB",
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          overflow: "hidden"
        }}
      >
        {/* Top Bar - Date Navigation & Show All Days */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px 24px",
            borderBottom: "1px solid #E2E8F0",
            position: "relative"
          }}
        >

          {/* Date Navigation - centered */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Box onClick={nextXWeeks} sx={{ cursor: "pointer", padding: "8px" }}>
              <ForwordIcon />
            </Box>
            <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1E293B" }}>
              {moment(currentWeek).subtract(6, "weeks").format("D MMM").replace(".", "'")} - {currentWeek.format("D MMM").replace(".", "'")}
            </Typography>
            <Box onClick={backXWeeks} sx={{ cursor: "pointer", padding: "8px" }}>
              <BackIcon />
            </Box>
          </Box>
          {/* Show All Days - positioned on the right */}
          <Button
            size="small"
            onClick={() => setShowAllDays(!showAllDays)}
            endIcon={showAllDays ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              position: "absolute",
              right: 24,
              fontSize: "12px",
              color: "#64748B",
              textTransform: "none",
              padding: "4px 12px",
              border: "1px solid #E2E8F0",
              borderRadius: "20px",
              "&:hover": {
                backgroundColor: "#F8FAFC"
              }
            }}
          >
            {showAllDays ? "הסתר ימים" : "הצג כל הימים"}
          </Button>
        </Box>

        {/* Table */}
        <Box>
          <Box sx={{ display: "flex", flexDirection: "column" }}>

            {/* Week Headers Row */}
            <Box sx={{ display: "flex", borderBottom: "1px solid #E2E8F0" }}>
              {/* Empty cell for row labels */}
              <Box sx={{ width: "80px", flexShrink: 0, padding: "12px" }} />

              {displayWeeks.map((week, i) => {
                const isEditing = editingWeekDate === week.weekDate;
                return (
                  <Box
                    key={week.weekDate}
                    sx={{
                      flex: 1,
                      padding: "12px 8px",
                      textAlign: "center",
                      backgroundColor: isEditing ? "#F1F5F9" : "transparent"
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1E293B" }}>
                        שבוע {displayWeeks.length - i}
                      </Typography>
                      {isEditing ? (
                        <>
                          <IconButton size="small" onClick={saveWeekEdits} sx={{ color: "#10B981" }}>
                            <Check fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={cancelEditing} sx={{ color: "#EF4444" }}>
                            <Close fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton size="small" onClick={() => startEditingWeek(week)} sx={{ color: "#64748B" }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: "13px", color: "#64748B", mt: 0.5 }}>
                      {formatDateRange(week)}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setShowAllDays(!showAllDays)}
                      endIcon={showAllDays ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{
                        mt: 1,
                        fontSize: "12px",
                        color: "#64748B",
                        textTransform: "none",
                        padding: "4px 12px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "20px",
                        "&:hover": {
                          backgroundColor: "#F8FAFC"
                        }
                      }}
                    >
                      {showAllDays ? "סגור ימים" : "הצג ימים"}
                    </Button>
                  </Box>
                );
              })}
            </Box>

            {/* Weight Average Row */}
            <Box sx={{ display: "flex", borderBottom: "1px solid #E2E8F0" }}>
              <Box
                sx={{
                  width: "80px",
                  flexShrink: 0,
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start"
                }}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}>
                  ממוצע שבועי
                </Typography>
              </Box>

              {displayWeeks.map((week) => (
                <Box
                  key={week.weekDate}
                  sx={{
                    flex: 1,
                    padding: "16px 8px",
                    textAlign: "center"
                  }}
                >
                  <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#1E293B" }}>
                    {getWeightAverage(week) ? `${getWeightAverage(week)} ק"ג` : "—"}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Daily Weights (Expandable) */}
            <Collapse in={showAllDays}>
              {DAYS.map((day) => (
                <Box key={day.key} sx={{ display: "flex", borderBottom: "1px solid #F1F5F9" }}>
                  <Box
                    sx={{
                      width: "100px",
                      flexShrink: 0,
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      backgroundColor: "#F8FAFC"
                    }}
                  >
                    <Typography sx={{ fontSize: "13px", color: "#64748B" }}>
                      {day.label}
                    </Typography>
                  </Box>

                  {displayWeeks.map((week) => {
                    const isEditing = editingWeekDate === week.weekDate;
                    const fieldName = day.key + "Weight";
                    const currentValue = isEditing ? editedWeekData?.[fieldName] : week[fieldName];
                    return (
                      <Box
                        key={week.weekDate}
                        sx={{
                          flex: 1,
                          padding: "8px",
                          textAlign: "center",
                          backgroundColor: isEditing ? "#F1F5F9" : "#F8FAFC"
                        }}
                      >
                        {isEditing ? (
                          <TextField
                            value={currentValue || ""}
                            onChange={(e) => updateEditedField(fieldName, e.target.value ? parseFloat(e.target.value) : null)}
                            type="number"
                            size="small"
                            inputProps={{ step: "0.1", style: { textAlign: "center", padding: "4px" } }}
                            sx={{ width: "80px", "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: "14px", color: "#1E293B" }}>
                            {week[fieldName] ? `${week[fieldName]} ק"ג` : "—"}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Collapse>

            {/* Trend Row */}
            <Box sx={{ display: "flex", borderBottom: "1px solid #E2E8F0" }}>
              <Box
                sx={{
                  width: "80px",
                  flexShrink: 0,
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start"
                }}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}>
                  מגמה שבועית
                </Typography>
              </Box>

              {displayWeeks.map((week, i) => {
                const trend = getTrend(i);
                const trendColor = getTrendColor(i);
                const isEditingThisWeek = editingWeekDate === week.weekDate;
                const weekData = isEditingThisWeek ? editedWeekData : week;
                const range = WeeklyRanges.find((x) => x.value === weekData?.weeklyRange);
                const currentGoal = weekData?.goal;
                return (
                  <Box
                    key={week.weekDate}
                    sx={{
                      flex: 1,
                      padding: "12px 8px",
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                      backgroundColor: isEditingThisWeek ? "#F1F5F9" : "transparent"
                    }}
                  >
                    {/* Trend Value with Icon */}
                    {trend !== null ? (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          backgroundColor: trendColor === "#10B981" ? "#D1FAE5" : "#FEE2E2",
                          padding: "4px 10px",
                          borderRadius: "16px"
                        }}
                      >
                        {parseFloat(trend) < 0 ? (
                          <TrendingDownIcon sx={{ color: trendColor, fontSize: 18 }} />
                        ) : (
                          <TrendingUpIcon sx={{ color: trendColor, fontSize: 18 }} />
                        )}
                        <Typography sx={{ fontSize: "12px", fontWeight: 600, color: trendColor }}>
                          {trend > 0 ? "+" : ""}{trend} ק"ג
                        </Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: "14px", color: "#64748B" }}>—</Typography>
                    )}
                    {/* יעד שבועי */}
                    {isEditingThisWeek ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography sx={{ fontSize: "12px", color: "#64748B" }}>יעד שבועי:</Typography>
                        <Select
                          value={weekData?.weeklyRange || ""}
                          onChange={(e) => updateEditedField("weeklyRange", e.target.value)}
                          size="small"
                          sx={{
                            minWidth: "120px",
                            backgroundColor: "#fff",
                            fontSize: "11px",
                            "& .MuiSelect-select": { padding: "4px 8px" }
                          }}
                        >
                          {WeeklyRanges.map((r) => (
                            <MenuItem
                              key={r.value}
                              value={r.value}
                              disabled={isRangeDisabledForGoal(r.value, currentGoal)}
                            >
                              {r.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
                        יעד שבועי: {range?.name || "—"}
                      </Typography>
                    )}
                    {/* יעד כללי badge */}
                    {isEditingThisWeek ? (
                      <Select
                        value={currentGoal || ""}
                        onChange={(e) => {
                          const goal = e.target.value;
                          // Auto-select the matching weekly range for the chosen goal.
                          const range = GOAL_DEFAULT_WEEKLY_RANGE[goal];
                          setEditedWeekData(prev => ({
                            ...prev,
                            goal,
                            ...(range ? { weeklyRange: range } : {}),
                          }));
                        }}
                        size="small"
                        sx={{
                          minWidth: "120px",
                          backgroundColor: "#fff",
                          fontSize: "11px",
                          "& .MuiSelect-select": { padding: "4px 8px" }
                        }}
                      >
                        {GoalTypes.map((g) => (
                          <MenuItem key={g.value} value={g.value}>{g.name}</MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          backgroundColor: "#D1FAE5",
                          padding: "3px 10px",
                          borderRadius: "12px"
                        }}
                      >
                        <Typography sx={{ fontSize: "11px", fontWeight: 500, color: "#10B981" }}>
                          יעד כללי: {goalLabels[week.goal] || "—"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Summary Section Header */}
            <Box sx={{ display: "flex", borderBottom: "1px solid #E2E8F0", backgroundColor: "#ffffff" }}>
              <Box
                sx={{
                  width: "80px",
                  flexShrink: 0,
                  padding: "12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "center"
                }}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1E293B" }}>
                  סיכום שבועי
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
                  יעדים ומדדים
                </Typography>
              </Box>

              {displayWeeks.map((week) => {
                const isEditingThisWeek = editingWeekDate === week.weekDate;
                const weekData = isEditingThisWeek ? editedWeekData : week;
                return (
                  <Box
                    key={week.weekDate}
                    sx={{
                      flex: 1,
                      padding: "8px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      backgroundColor: isEditingThisWeek ? "#F1F5F9" : "transparent"
                    }}
                  >
                    <MetricCard
                      type="calories"
                      average={getCalorieAverage(week.weekDate)}
                      goal={weekData?.caloriesGoal}
                      dailyData={DAYS.map(day => {
                        const log = calorieData[week.weekDate]?.dailyLogs?.find(
                          l => l.dayOfWeek?.toLowerCase() === day.key
                        );
                        return { day: day.label, value: log?.menu?.nutritionSummary?.calories, fieldName: day.key };
                      })}
                      expanded={showCaloriesDetails}
                      onToggleExpand={() => setShowCaloriesDetails(!showCaloriesDetails)}
                      isEditing={isEditingThisWeek}
                      onGoalChange={updateEditedField}
                      goalFieldName="caloriesGoal"
                    />
                    <MetricCard
                      type="steps"
                      average={getStepsAverage(weekData)}
                      goal={weekData?.stepsGoal}
                      dailyData={DAYS.map(day => ({ day: day.label, value: weekData?.[day.key + "Steps"], fieldName: day.key + "Steps" }))}
                      expanded={showStepsDetails}
                      onToggleExpand={() => setShowStepsDetails(!showStepsDetails)}
                      isEditing={isEditingThisWeek}
                      onFieldChange={updateEditedField}
                      onGoalChange={updateEditedField}
                      goalFieldName="stepsGoal"
                    />
                    <MetricCard
                      type="sleep"
                      average={getSleepAverage(weekData)}
                      goal={weekData?.sleepGoalMinutes}
                      dailyData={DAYS.map(day => ({ day: day.label, value: weekData?.[day.key + "Sleep"], fieldName: day.key + "Sleep" }))}
                      expanded={showSleepDetails}
                      onToggleExpand={() => setShowSleepDetails(!showSleepDetails)}
                      isEditing={isEditingThisWeek}
                      onFieldChange={updateEditedField}
                      onGoalChange={updateEditedField}
                      goalFieldName="sleepGoalMinutes"
                    />
                  </Box>
                );
              })}
            </Box>

          </Box>
        </Box>
      </Box>

      {/* Goals Edit Modal - hidden, using inline editing instead
      <GoalsEditModal
        open={goalsModalOpen}
        onClose={closeGoalsModal}
        weekData={editingWeekData}
        onWeekDataChange={handleWeekDataChange}
        onSave={saveGoals}
      />
      */}
    </Box>
  );
};

export default ClientWeightTrackPage;
